import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {Tab, Tabs} from "@mui/material";

export type PageTab = {
    tab: string,
    label: string,
    content: () => JSX.Element,
    disabled?: boolean
}

export type PageTabsProps = {
    tabs: PageTab[],
    currentTab?: string
    onTabChange?: (tab: string) => void
}

const indexOfTab = (tabs: PageTab[], tab?: string) => {
    const found = tabs.findIndex(t => t.tab === tab);
    return found === -1 ? 0 : found;
};

export const PageTabs = ({tabs, currentTab, onTabChange}: PageTabsProps) => {
    const [value, setValue] = useState(() => indexOfTab(tabs, currentTab));

    // currentTab was read once at mount, so a tab changed by navigation rather
    // than by a click left the URL and the rendered tab disagreeing.
    useEffect(() => {
        if (currentTab === undefined) return;
        const next = tabs.findIndex(t => t.tab === currentTab);
        // An unrecognised value means the caller is tracking something we do
        // not render; snapping to the first tab would fight the user's click.
        if (next === -1) return;
        setValue(next);
        // tabs is rebuilt on every render by most callers, so keying the effect
        // on it would reset the tab on any parent re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTab]);

    const handleChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        if (onTabChange) {
            onTabChange(tabs[newValue].tab);
        }
        setValue(newValue);
    }, [tabs, onTabChange]);

    return <>
        {/* In a flex column a tall tab body shrinks the bar, its border clips the tabs by 1px
            and MUI then shows scroll buttons although nothing overflows. */}
        <Tabs key={'tabs'} value={value} onChange={handleChange} scrollButtons={'auto'} variant={'scrollable'} allowScrollButtonsMobile sx={{flexShrink: 0}}>
            {tabs.map((tab, i) => {
                return <Tab label={tab.label} key={i} disabled={!!tab.disabled}/>
            })}
        </Tabs>
        {tabs[value]?.content()}
    </>;
}
